/* eslint-disable no-console */

export interface FileWithPath extends File {
  // webkitRelativePath?: string;
  fullPath?: string;
}

const traverseFileTree = async (item: FileSystemEntry, path = ''): Promise<FileWithPath[]> => {
  const files: FileWithPath[] = [];

  try {
    if (item.isFile) {
      const fileEntry = item as FileSystemFileEntry;
      try {
        const file = await new Promise<File>((resolve, reject) => {
          fileEntry.file(resolve, reject);
        });

        const fileWithPath = file as FileWithPath;
        fileWithPath.fullPath = path + file.name;
        files.push(fileWithPath);
      } catch (error) {
        console.warn(`Failed to read file: ${path}${item.name}`, error);
      }
    } else if (item.isDirectory) {
      const dirEntry = item as FileSystemDirectoryEntry;
      const dirReader = dirEntry.createReader();

      try {
        const entries = await new Promise<FileSystemEntry[]>((resolve, reject) => {
          const allEntries: FileSystemEntry[] = [];

          const readEntries = () => {
            dirReader.readEntries((batchEntries) => {
              if (batchEntries.length === 0) {
                resolve(allEntries);
              } else {
                allEntries.push(...batchEntries);
                readEntries();
              }
            }, reject);
          };

          readEntries();
        });

        const nestedFilesArrays = await Promise.all(
          entries.map(async (entry) => {
            try {
              return await traverseFileTree(entry, `${path}${item.name}/`);
            } catch (error) {
              console.warn(`Failed to traverse entry: ${entry.name}`, error);
              return [];
            }
          }),
        );
        files.push(...nestedFilesArrays.flat());
      } catch (error) {
        console.warn(`Failed to read directory: ${path}${item.name}`, error);
      }
    }
  } catch (error) {
    console.warn(`Unexpected error traversing file tree: ${item.name}`, error);
  }

  return files;
};

export const getFilesFromDataTransfer = async (
  dataTransfer: DataTransfer,
): Promise<FileWithPath[]> => {
  const files: FileWithPath[] = [];
  const items = Array.from(dataTransfer.items || []);
  const filesList = Array.from(dataTransfer.files || []);

  if (items.length === 0) {
    const fallbackFiles = Array.from(dataTransfer.files || []).map((file) => {
      const fileWithPath = file as FileWithPath;
      fileWithPath.fullPath = file.name;
      return fileWithPath;
    });
    return fallbackFiles;
  }

  const processedFiles = await Promise.all(
    items.map(async (item) => {
      const entry = item.webkitGetAsEntry?.();

      if (entry) {
        try {
          return await traverseFileTree(entry);
        } catch (error) {
          console.warn(`Failed to traverse item: ${entry.name}`, error);
          return [];
        }
      } else {
        const file = item.getAsFile();
        if (file) {
          const fileWithPath = file as FileWithPath;
          fileWithPath.fullPath = file.name;
          return [fileWithPath];
        }
        return [];
      }
    }),
  );
  files.push(...processedFiles.flat());

  // Fallback: If we only got 1 file from items but filesList (captured at start) has more,
  // use filesList instead (workaround for browser bug on macOS)
  if (files.length <= 1 && filesList.length > 1) {
    const fallbackFiles = filesList.map((file) => {
      const fileWithPath = file as FileWithPath;
      fileWithPath.fullPath = file.webkitRelativePath || file.name;
      return fileWithPath;
    });
    return fallbackFiles;
  }

  return files;
};
